import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import EntityTable from '../../components/workspace/EntityTable';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';

export default function Branches() {
  const { appId } = useParams();
  const api = useApi();
  const { canWrite } = useWorkspace();

  // Branches need lookup options (locations + parent branches). Load both.
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [locs, brs] = await Promise.all([
        api.locations.list(appId).catch(() => ({ items: [] })),
        api.branches.list(appId).catch(() => ({ items: [] })),
      ]);
      if (cancelled) return;
      setLocations(locs.items || []);
      setBranches(brs.items || []);
    })();
    return () => { cancelled = true; };
  }, [api, appId]);

  const fetcher = useCallback(() => api.branches.list(appId), [api, appId]);

  const locationLookup = locations.map((l) => ({ value: String(l.id), label: l.name }));
  const branchLookup   = branches.map((b)   => ({ value: String(b.id), label: b.name }));

  const COLUMNS = [
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'Code' },
    {
      key: 'location_id',
      label: 'Location',
      render: (r) =>
        locations.find((l) => l.id === r.location_id)?.name ||
        <span className="muted">—</span>,
    },
    {
      key: 'parent_branch_id',
      label: 'Parent branch',
      render: (r) =>
        branches.find((b) => b.id === r.parent_branch_id)?.name ||
        <span className="muted">—</span>,
    },
  ];

  const FIELDS = [
    { name: 'name',             label: 'Name',          required: true, maxLen: 200, half: true },
    { name: 'code',             label: 'Code',          maxLen: 50, help: 'Optional short code (must be unique)', half: true },
    { name: 'location_id',      label: 'Location',      type: 'lookup', lookupOptions: locationLookup, half: true },
    { name: 'parent_branch_id', label: 'Parent branch', type: 'lookup', lookupOptions: branchLookup, half: true },
    { name: 'address_line1',    label: 'Address line 1', maxLen: 200, half: true },
    { name: 'address_line2',    label: 'Address line 2', maxLen: 200, half: true },
    { name: 'city',             label: 'City',           maxLen: 100, half: true },
    { name: 'postal_code',      label: 'Postal code',    maxLen: 20,  half: true },
    { name: 'timezone',         label: 'Timezone',       maxLen: 64, placeholder: 'e.g. Asia/Kolkata' },
    { name: 'description',      label: 'Description',   type: 'textarea', rows: 3, maxLen: 2000 },
  ];

  return (
    <EntityTable
      title="Branches"
      fetcher={fetcher}
      columns={COLUMNS}
      formFields={FIELDS}
      onCreate={(body)      => api.branches.create(appId, body)}
      onUpdate={(id, body)  => api.branches.update(appId, id, body)}
      onDelete={(id)        => api.branches.remove(appId, id)}
      canWrite={canWrite}
      emptyMessage="No branches yet."
      onRowsLoaded={setBranches}
    />
  );
}
